import { API_PATH_INTERNAL } from '@/lib/constants'
import apiService from '@/lib/services/apiService'
import {
  CreateLocationRequest,
  GetLocationMeetingsParams,
  GetLocationUsersParams,
  UpdateLocationRequest,
} from '@/types/requests/locations'
import {
  LocationCollection,
  LocationCountryCollection,
  LocationDeletionImpact,
  LocationDetailsItem,
  LocationItem,
  LocationMeetingCollection,
  LocationUserCollection,
} from '@/types/responses/locations'

class LocationService {
  private readonly endpoint = `${API_PATH_INTERNAL}/locations`

  createIri(companyId: number | string) {
    return `${this.endpoint}/${companyId}`
  }

  async getLocations(): Promise<LocationCollection> {
    const response = await apiService.get(this.endpoint)

    return response.data
  }

  async getLocation(locationId: number): Promise<LocationDetailsItem> {
    const response = await apiService.get(`${this.endpoint}/${locationId}`)

    return response.data
  }

  async getLocationCountries(): Promise<LocationCountryCollection> {
    const response = await apiService.get(
      `${API_PATH_INTERNAL}/location-countries`,
    )

    return response.data
  }

  async getLocationUsers(
    locationId: number,
    params: GetLocationUsersParams,
  ): Promise<LocationUserCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${locationId}/users`,
      { params },
    )

    return response.data
  }

  async getLocationMeetings(
    locationId: number,
    params: GetLocationMeetingsParams,
  ): Promise<LocationMeetingCollection> {
    const response = await apiService.get(
      `${this.endpoint}/${locationId}/meetings`,
      { params },
    )

    return response.data
  }

  async getLocationDeletionImpact(
    locationId: number,
  ): Promise<LocationDeletionImpact> {
    const response = await apiService.get(
      `${this.endpoint}/${locationId}/deletion-impact`,
    )

    return response.data
  }

  async createLocation(request: CreateLocationRequest): Promise<LocationItem> {
    const response = await apiService.post<LocationItem>(this.endpoint, request)

    return response.data
  }

  async updateLocation(
    locationId: number,
    request: UpdateLocationRequest,
  ): Promise<LocationDetailsItem> {
    const response = await apiService.patch<LocationDetailsItem>(
      `${this.endpoint}/${locationId}`,
      request,
    )

    return response.data
  }

  async deleteLocation(locationId: number): Promise<void> {
    await apiService.delete(`${this.endpoint}/${locationId}`)
  }
}

const locationService = new LocationService()

export default locationService
